import { DatePickerChanges, ISO_DATE_FORMAT } from '@rx-applications/shared/utils';
import { addYears, format, subDays, subYears } from 'date-fns';

export interface ClientField {
  key: string;
  value: string;
}
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */
export interface BookingTableRow {
  ReservationId: string;
  ReservationGuid: string;
  HotelName: string;
  HotelId: string;
  LeadName: string;
  NumberOfNights: number;
  CheckInDate: string;
  CheckOutDate: string;
  SellPrice: string;
  CurrencyCode: string;
  Username: string;
  Status: string;
  IsNonRefundable: boolean;
  AllowResend: boolean;
  CreatedOn: string;
  AllowCancel: boolean;
  AllowSpecialRequest: boolean;
  AllowPdf: boolean;
  NumberOfRooms: number;
  Meals: string;
  RowDetails: {
    HotelAddress: string;
    GuestsNames: string;
    ClientFields: ClientField[];
    ThumbnailUrl: string;
  };
}

export interface BookingsDatePickerChanges extends DatePickerChanges {
  dateType: BookingsDateTypes;
}

export enum BookingsDateTypes {
  Booking = 'creationDate',
  CheckIn = 'arrivalDate',
  CheckOut = 'departureDate',
}
/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
export enum BookingsTableDefaults {
  RowOffset = 0,
  PageNumber = 1,
  PageSize = 10,
  SortField = 'CreatedOn',
  SortAscending = 1,
  SortDescending = -1,
}

export interface BookingsTableColumn {
  field: string;
  visible: boolean;
  filter?: string;
  header?: string;
  sortable?: boolean;
  type?: string;
  resizable?: boolean;
}

export enum BookingsSearchSortDirection {
  ASC = 'Ascending',
  DESC = 'Descending',
}

export interface BookingPresetModel {
  Name: string;
  Classname: string;
  SortBy: string;
  SortDirection: BookingsSearchSortDirection;
  Statuses: string[];
  DateType: BookingsDateTypes;
  StartDate: string;
  EndDate: string;
  HotelName: string;
  ReservationId: string;
  PassangerName: string;
  Usernames: string[];
  Total: number;
}

export interface AdvancedSearchFields {
  passengerName?: string;
  username?: string[];
}

export interface BookingsReportRequest extends AdvancedSearchFields {
  websiteGuid: string;
  reservationID?: string;
  dateType: BookingsDateTypes;
  startDate: string;
  endDate: string;
  pageSize: number;
  pageNumber: number;
  sortBy: string;
  sortDirection: BookingsSearchSortDirection;
}

export class BookingsSearch implements BookingsReportRequest {
  public websiteGuid = '';
  public pageNumber: number = BookingsTableDefaults.PageNumber;
  public pageSize: number = BookingsTableDefaults.PageSize;
  public reservationId?: string;
  public sortBy = 'ReservationId';
  public sortDirection: BookingsSearchSortDirection = BookingsSearchSortDirection.DESC;
  public passengerName?: string;
  public username?: string[];
  public dateType: BookingsDateTypes = BookingsDateTypes.Booking;
  public startDate = datePickerOptions.initialStartDate;
  public endDate = datePickerOptions.dateNow;
  public bookingStatuses?: string[];
  public hotelName?: string;
  public datequery?: string;
}

export interface BookingsReportTotalRequest {
  websiteGuid: string;
  bookingStatuses?: string[];
  datequery?: string;
}

export interface BookingsReportResponse {
  data: BookingReport[];
  uniqueUsernames: string[];
  _paging: Paging;
}

export interface Paging {
  pageSize: number;
  pageNumber: number;
  total: number;
  startingIndex?: number;
  closingIndex?: number;
}

export interface Customer {
  Name: string;
  Email: string;
  Phone: string;
  Address1: string;
  Address2: string;
  Postcode: string;
  City: string;
  CountryCode: string;
  CountryName: string;
}

export interface BookingHotel {
  Name: string;
  CityName: string;
  Address: string;
  CountryCode: string;
  PhoneNumber: string;
  StarRating: number;
}

export interface BookingHotelAutocompleteElement extends BookingHotel {
  index?: number;
  count: number;
  hotelId: number;
}

export interface BookingReport {
  CheckInDate: string;
  CheckOutDate: string;
  ClientField_Title1: string;
  ClientField_Title2: string;
  ClientField_Title3: string;
  ClientField_Title4: string;
  ClientField_Title5: string;
  ClientField_Title6: string;
  ClientField_Title7: string;
  ClientField_Title8: string;
  ClientField_Title9: string;
  ClientField_Title10: string;
  ClientField_Value1: string;
  ClientField_Value2: string;
  ClientField_Value3: string;
  ClientField_Value4: string;
  ClientField_Value5: string;
  ClientField_Value6: string;
  ClientField_Value7: string;
  ClientField_Value8: string;
  ClientField_Value9: string;
  ClientField_Value10: string;
  CreatedOn: string;
  CurrencyCode: string;
  Customer: Customer;
  DateTermsCommence: string;
  Hotel: BookingHotel;
  HotelId: number;
  IsNonRefundable: boolean;
  IsOnRequest: boolean;
  LeadName: string;
  ModifiedOn: string;
  NumberOfNights: number;
  NumberOfRooms: number;
  PaymentMade: number;
  PaymentOutstanding: number;
  ReservationGuid: string;
  ReservationId: string;
  Rooms: BookingRoom[];
  Status: string;
  Total: number;
  Username: string;
  ThumbnailUrl: string;
  ApprovalReason: string;
  Approver: string;
  QuoteId: string;
}

export type BookingReportWithoutApproval = Omit<BookingReport, 'ApprovalReason' | 'Approver' | 'QuoteId'>;


export type OneOfBookingReport = BookingReportWithoutApproval | BookingReport;


export interface BookingRoom {
  MealName: string;
  NumberOfAdults: number;
  NumberOfChildren: number;
  Passengers: BookingPassenger[];
  RoomDescription: string;
  RoomName: string;
}

export interface BookingPassenger {
  Title: string;
  FirstName: string;
  Surname: string;
}

export const datePickerOptions = {
  initialStartDate: format(subDays(new Date(), 30), ISO_DATE_FORMAT),
  dateNow: format(new Date(), ISO_DATE_FORMAT),
  oneYearFromNow: format(addYears(new Date(), 1), ISO_DATE_FORMAT),
  eightYearsAgo: format(subYears(new Date(), 8), ISO_DATE_FORMAT),
};

export interface BookingReportSheet {
  CheckInDate: string;
  CheckOutDate: string;
  ClientField_Title1?: string;
  ClientField_Title2?: string;
  ClientField_Title3?: string;
  ClientField_Title4?: string;
  ClientField_Title5?: string;
  ClientField_Title6?: string;
  ClientField_Title7?: string;
  ClientField_Title8?: string;
  ClientField_Title9?: string;
  ClientField_Title10?: string;
  ClientField_Value1?: string;
  ClientField_Value2?: string;
  ClientField_Value3?: string;
  ClientField_Value4?: string;
  ClientField_Value5?: string;
  ClientField_Value6?: string;
  ClientField_Value7?: string;
  ClientField_Value8?: string;
  ClientField_Value9?: string;
  ClientField_Value10?: string;
  CreatedOn: string;
  CurrencyCode: string;
  Customer: {
    Name: string;
    Email: string;
    Phone: string;
  };
  Hotel: {
    Name: string;
    CityName: string;
    Address: string;
    PhoneNumber: string;
  };
  LeadName: string;
  PaymentOutstanding: number | string;
  ReservationId: string;
  Status: string;
  Total: number | string;
  Username: string;
  NumberOfNights: number;
  NumberOfRooms: number;
  Guests?: string;
  Rooms?: BookingRoom[];
  NumberOfGuests?: number;
  Meals?: string;
}

export interface RowExpansionEvent {
  data: BookingTableRow;
  originalEvent: MouseEvent;
}

export class BookingTableRow {
  ReservationId!: string;
  ReservationGuid!: string;
  HotelName!: string;
  HotelId!: string;
  LeadName!: string;
  NumberOfNights!: number;
  CheckInDate!: string;
  CheckOutDate!: string;
  SellPrice!: string;
  CurrencyCode!: string;
  Username!: string;
  CustomerName!: string;
  Status!: string;
  IsNonRefundable!: boolean;
  AllowResend!: boolean;
  CreatedOn!: string;
  AllowCancel!: boolean;
  AllowSpecialRequest!: boolean;
  AllowPdf!: boolean;
  NumberOfRooms!: number;
  Meals!: string;
  RowDetails!: {
    HotelAddress: string;
    GuestsNames: string;
    ClientFields: ClientField[];
    ThumbnailUrl: string;
  };
}

export interface ClientField {
  key: string;
  value: string;
}
